import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Sunset, Moon, Check, Edit2, RefreshCw, Loader2, X, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';

const SLOT_ICONS = {
  morning: Sun,
  midday: Sunset,
  evening: Moon,
};

const SLOT_LABELS = {
  en: { morning: 'Morning', midday: 'Midday', evening: 'Evening' },
  tr: { morning: 'Sabah', midday: 'Öğle', evening: 'Akşam' },
};

const SLOT_ACCENT = {
  morning: 'from-amber-400/15 to-amber-500/5 border-amber-400/30 text-amber-300',
  midday: 'from-orange-400/15 to-orange-500/5 border-orange-400/30 text-orange-300',
  evening: 'from-indigo-400/15 to-indigo-500/5 border-indigo-400/30 text-indigo-300',
};

/**
 * Today's Path — Daily Execution Navigator (Phase 2)
 * A 3-block plan grounded in the morning One Win + 30-day memory + patterns.
 * Tap to complete. Edit inline. Regenerate anytime. Auto-closes after evening.
 */
const TodayPath = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(false);
  const [plan, setPlan] = useState(null);
  const [eveningSubmitted, setEveningSubmitted] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftAction, setDraftAction] = useState('');

  const fetchPlan = useCallback(async () => {
    try {
      const res = await api.get('/day-plan/today');
      setAvailable(res.data.available);
      setPlan(res.data.plan || null);
      setEveningSubmitted(!!res.data.evening_submitted);
    } catch (e) {
      console.error('Day plan fetch failed', e);
      setAvailable(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const toggleComplete = async (block) => {
    const newVal = !block.completed;
    // optimistic
    setPlan((p) => ({
      ...p,
      blocks: p.blocks.map((b) =>
        b.id === block.id ? { ...b, completed: newVal, completed_at: newVal ? new Date().toISOString() : null } : b
      ),
    }));
    try {
      await api.post('/day-plan/block/complete', { block_id: block.id, completed: newVal });
      if (newVal) window.dispatchEvent(new Event('signal:celebrate'));
    } catch (e) {
      // revert
      setPlan((p) => ({
        ...p,
        blocks: p.blocks.map((b) =>
          b.id === block.id ? { ...b, completed: !newVal } : b
        ),
      }));
      toast.error(language === 'tr' ? 'Kaydedilemedi' : 'Failed to save');
    }
  };

  const startEdit = (block) => {
    setEditingId(block.id);
    setDraftTitle(block.title);
    setDraftAction(block.action);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraftTitle('');
    setDraftAction('');
  };

  const saveEdit = async (block) => {
    const title = draftTitle.trim();
    const action = draftAction.trim();
    if (!title || !action) {
      toast.error(language === 'tr' ? 'Boş bırakılamaz' : 'Cannot be empty');
      return;
    }
    setPlan((p) => ({
      ...p,
      blocks: p.blocks.map((b) =>
        b.id === block.id ? { ...b, title, action, edited: true } : b
      ),
    }));
    cancelEdit();
    try {
      await api.post('/day-plan/block/edit', { block_id: block.id, title, action });
      toast.success(language === 'tr' ? 'Güncellendi' : 'Updated');
    } catch (e) {
      toast.error(language === 'tr' ? 'Kaydedilemedi' : 'Failed to save');
      fetchPlan();
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const res = await api.post('/day-plan/generate');
      setPlan(res.data.plan);
      toast.success(language === 'tr' ? 'Yol yeniden çizildi' : 'Path redrawn');
    } catch (e) {
      toast.error(language === 'tr' ? 'Yenileme başarısız' : 'Regenerate failed');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 mb-6" data-testid="today-path-loading">
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
        </div>
      </div>
    );
  }

  if (!available || !plan || !plan.blocks?.length) {
    return null;
  }

  const completedCount = plan.blocks.filter((b) => b.completed).length;
  const totalCount = plan.blocks.length;
  const allDone = completedCount === totalCount;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.45, duration: 0.4 }}
      className={`rounded-2xl border ${
        eveningSubmitted
          ? 'border-emerald-400/20 bg-gradient-to-b from-emerald-500/[0.03] to-transparent'
          : 'border-[#D4AF37]/20 bg-gradient-to-b from-[#D4AF37]/[0.04] to-transparent'
      } overflow-hidden mb-6`}
      data-testid="today-path-card"
    >
      {/* Header */}
      <div className="px-5 sm:px-6 pt-5 pb-3 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/70 font-sans mb-1.5">
            {language === 'tr' ? "Bugünün Yolu" : "Today's Path"}
          </p>
          {plan.headline && (
            <p className="font-serif text-lg sm:text-xl leading-snug text-[#F8F5F2]" data-testid="today-path-headline">
              {plan.headline}
            </p>
          )}
          <p className="text-[11px] text-[#6B7280] font-sans mt-1.5">
            {language === 'tr'
              ? `${completedCount}/${totalCount} blok tamamlandı`
              : `${completedCount}/${totalCount} blocks complete`}
            {eveningSubmitted && (
              <span className="ml-2 text-emerald-400/80">
                · {language === 'tr' ? 'Gün kapandı' : 'Day closed'}
              </span>
            )}
          </p>
        </div>
        {!eveningSubmitted && (
          <button
            onClick={regenerate}
            disabled={regenerating}
            className="shrink-0 flex items-center gap-1 text-[10px] tracking-wider uppercase font-sans text-[#A1A1AA] hover:text-[#D4AF37] transition-colors disabled:opacity-40"
            title={language === 'tr' ? 'Yolu yenile' : 'Regenerate path'}
            data-testid="today-path-regenerate"
          >
            {regenerating ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            <span className="hidden sm:inline">{language === 'tr' ? 'Yenile' : 'Redraw'}</span>
          </button>
        )}
      </div>

      {/* Blocks */}
      <div className="px-3 sm:px-4 pb-4 space-y-2.5">
        {plan.blocks.map((block, idx) => {
          const Icon = SLOT_ICONS[block.slot] || Sun;
          const slotLabel = SLOT_LABELS[language]?.[block.slot] || block.label;
          const isEditing = editingId === block.id;
          const accent = SLOT_ACCENT[block.slot] || SLOT_ACCENT.morning;

          return (
            <motion.div
              key={block.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + idx * 0.08 }}
              className={`rounded-xl border bg-gradient-to-br ${accent} ${
                block.completed ? 'opacity-60' : ''
              } transition-all`}
              data-testid={`today-path-block-${block.slot}`}
            >
              <div className="p-3.5 sm:p-4">
                <div className="flex items-start gap-3">
                  {/* Slot icon */}
                  <div className={`shrink-0 mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center bg-white/5 border border-white/10`}>
                    <Icon className="w-4 h-4" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] uppercase tracking-[0.2em] font-sans text-[#A1A1AA] mb-1">
                      {slotLabel}
                      {block.edited && (
                        <span className="ml-2 text-[#D4AF37]/60 normal-case tracking-normal">
                          {language === 'tr' ? '· düzenlendi' : '· edited'}
                        </span>
                      )}
                    </p>

                    {!isEditing ? (
                      <>
                        <p
                          className={`font-serif text-base sm:text-lg leading-snug ${
                            block.completed ? 'text-[#A1A1AA] line-through decoration-white/20' : 'text-[#F8F5F2]'
                          }`}
                          data-testid={`today-path-block-title-${block.slot}`}
                        >
                          {block.title}
                        </p>
                        <p
                          className={`text-sm font-sans mt-1.5 leading-relaxed ${
                            block.completed ? 'text-[#6B7280] line-through decoration-white/10' : 'text-[#D1D1D6]'
                          }`}
                          data-testid={`today-path-block-action-${block.slot}`}
                        >
                          {block.action}
                        </p>
                        {block.frame && !block.completed && (
                          <p className="text-[12px] italic font-sans text-[#A1A1AA]/80 mt-2 leading-relaxed">
                            {block.frame}
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={draftTitle}
                          onChange={(e) => setDraftTitle(e.target.value)}
                          maxLength={140}
                          className="w-full bg-black/30 border border-[#D4AF37]/30 rounded-md px-2.5 py-1.5 text-[#F8F5F2] font-serif text-base focus:outline-none focus:border-[#D4AF37]/60"
                          placeholder={language === 'tr' ? 'Başlık' : 'Title'}
                          data-testid={`today-path-edit-title-${block.slot}`}
                        />
                        <textarea
                          value={draftAction}
                          onChange={(e) => setDraftAction(e.target.value)}
                          maxLength={280}
                          rows={2}
                          className="w-full bg-black/30 border border-[#D4AF37]/30 rounded-md px-2.5 py-1.5 text-[#D1D1D6] font-sans text-sm focus:outline-none focus:border-[#D4AF37]/60 resize-none"
                          placeholder={language === 'tr' ? 'İlk hareket' : 'First move'}
                          data-testid={`today-path-edit-action-${block.slot}`}
                        />
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="shrink-0 flex flex-col gap-1.5">
                    {!isEditing ? (
                      <>
                        <button
                          onClick={() => toggleComplete(block)}
                          className={`w-9 h-9 rounded-full flex items-center justify-center transition-all border ${
                            block.completed
                              ? 'bg-emerald-500/30 border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/40'
                              : 'bg-transparent border-white/20 text-[#A1A1AA] hover:border-[#D4AF37]/60 hover:text-[#D4AF37]'
                          }`}
                          title={block.completed
                            ? (language === 'tr' ? 'Geri al' : 'Undo')
                            : (language === 'tr' ? 'Tamamla' : 'Complete')}
                          data-testid={`today-path-block-complete-${block.slot}`}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        {!eveningSubmitted && !block.completed && (
                          <button
                            onClick={() => startEdit(block)}
                            className="w-9 h-9 rounded-full flex items-center justify-center border border-white/10 text-[#6B7280] hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-all"
                            title={language === 'tr' ? 'Düzenle' : 'Edit'}
                            data-testid={`today-path-block-edit-${block.slot}`}
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => saveEdit(block)}
                          className="w-9 h-9 rounded-full flex items-center justify-center bg-[#D4AF37]/20 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/30"
                          title={language === 'tr' ? 'Kaydet' : 'Save'}
                          data-testid={`today-path-block-save-${block.slot}`}
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="w-9 h-9 rounded-full flex items-center justify-center border border-white/10 text-[#6B7280] hover:text-rose-400 hover:border-rose-400/30"
                          title={language === 'tr' ? 'İptal' : 'Cancel'}
                          data-testid={`today-path-block-cancel-${block.slot}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Footer note when all done */}
      <AnimatePresence>
        {allDone && !eveningSubmitted && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mx-4 sm:mx-5 mb-4 -mt-1 px-3 py-2.5 rounded-lg bg-emerald-500/10 border border-emerald-400/30"
            data-testid="today-path-all-complete"
          >
            <p className="text-xs text-emerald-300 font-sans text-center">
              {language === 'tr'
                ? 'Yol tamamlandı. Akşam kapanışı seni bekliyor.'
                : 'The path is walked. The evening close awaits.'}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default TodayPath;
